import * as React from "react";

interface ScreenReaderAnnouncerProps {
  politeMessage?: string;
  assertiveMessage?: string;
}

type JourneyLike = {
  Departure: string;
  Arrival: string;
  TravelTime: string;
  Switch: string | number;
  RouteLinks?: Array<{
    Line?: {
      LineName?: string;
    };
  }> | null;
  BookedGroupJourneys?: number;
};

interface JourneySrLabelOptions {
  isBooked?: boolean;
  includeLines?: boolean;
}

export function formatDurationForSr(value: string) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return value;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours === 0) {
    return `${minutes} minuter`;
  }

  if (minutes === 0) {
    return `${hours} ${hours === 1 ? "timme" : "timmar"}`;
  }

  return `${hours} ${hours === 1 ? "timme" : "timmar"} och ${minutes} minuter`;
}

export function getJourneyLineNamesForSr(result: JourneyLike) {
  if (!Array.isArray(result.RouteLinks) || result.RouteLinks.length === 0) {
    return "";
  }

  return result.RouteLinks.map((item) => item.Line?.LineName)
    .filter(Boolean)
    .join(", ");
}

export function getJourneySrLabel(
  result: JourneyLike,
  formatDate: (date: string, format: string) => string,
  options: JourneySrLabelOptions = {}
) {
  const departure = formatDate(result.Departure, "HH:mm");
  const arrival = formatDate(result.Arrival, "HH:mm");
  const switches = parseInt(String(result.Switch), 10) || 0;
  const lineNames =
    options.includeLines === false ? "" : getJourneyLineNamesForSr(result);

  return [
    `Avgång ${departure}.`,
    `Ankomst ${arrival}.`,
    `Restid ${formatDurationForSr(result.TravelTime)}.`,
    `${switches} ${switches === 1 ? "byte" : "byten"}.`,
    lineNames ? `Linje ${lineNames}.` : "",
    options.isBooked ? "Resan är fullbokad." : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export default function ScreenReaderAnnouncer({
  politeMessage = "",
  assertiveMessage = "",
}: ScreenReaderAnnouncerProps) {
  const [politeText, setPoliteText] = React.useState("");
  const [assertiveText, setAssertiveText] = React.useState("");

  React.useEffect(() => {
    if (!politeMessage) {
      setPoliteText("");
      return;
    }

    setPoliteText("");

    const id = window.requestAnimationFrame(() => {
      setPoliteText(politeMessage);
    });

    return () => window.cancelAnimationFrame(id);
  }, [politeMessage]);

  React.useEffect(() => {
    if (!assertiveMessage) {
      setAssertiveText("");
      return;
    }

    setAssertiveText("");

    const id = window.requestAnimationFrame(() => {
      setAssertiveText(assertiveMessage);
    });

    return () => window.cancelAnimationFrame(id);
  }, [assertiveMessage]);

  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {politeText}
      </div>

      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {assertiveText}
      </div>
    </>
  );
}
